import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, Validators } from '@angular/forms';
import { MatTable } from '@angular/material/table';

// Importações da API gerada
import { Product, ProductControllerService } from '../../../core/generated';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-admin-product-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">{{ data.product ? 'Editar Produto' : 'Novo Produto' }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <form [formGroup]="productForm">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Nome do Produto</mat-label>
                <input matInput formControlName="name" placeholder="Ex: Consulta Médica">
                <mat-error *ngIf="productForm.get('name')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Preço Base</mat-label>
                <input matInput formControlName="basePrice" appCurrencyMask autocomplete="off">
                <mat-error *ngIf="productForm.get('basePrice')?.hasError('required')">Campo obrigatório</mat-error>
                <mat-error *ngIf="productForm.get('basePrice')?.hasError('min')">O preço deve ser maior ou igual a zero</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Descrição</mat-label>
                <textarea matInput formControlName="description" rows="3" maxlength="255" placeholder="Descreva o produto..."></textarea>
                <mat-hint>Descrição detalhada do produto</mat-hint>
              </mat-form-field>

              <mat-checkbox formControlName="active">Produto Ativo</mat-checkbox>
            </form>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onCancel()">
              <mat-icon>close</mat-icon> Cancelar
            </button>
            <button mat-flat-button color="primary" class="btn btn-primary" (click)="onSave()" [disabled]="productForm.invalid">
              <mat-icon>save</mat-icon> Salvar
            </button>
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    mat-form-field { 
      width: 100%; 
      margin-bottom: var(--spacing-sm); 
    }
    mat-checkbox {
      margin-bottom: var(--spacing-md);
    }
  `]
})
export class AdminProductDialogComponent {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AdminProductDialogComponent>);

  productForm = this.fb.group({
    id: [null as number | null],
    name: ['', Validators.required],
    basePrice: [0, [Validators.required, Validators.min(0)]],
    description: [''],
    active: [true],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { product?: Product }) {
    if (this.data.product) {
      this.productForm.patchValue({
        id: this.data.product.id ?? null,
        name: this.data.product.name ?? '',
        basePrice: this.data.product.basePrice ?? 0,
        description: this.data.product.description ?? '',
        active: this.data.product.active ?? true,
      });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.productForm.valid) {
      const product: Product = {
        id: this.productForm.value.id ?? undefined,
        name: this.productForm.value.name!,
        basePrice: this.productForm.value.basePrice!,
        description: this.productForm.value.description || undefined,
        active: this.productForm.value.active ?? true,
      };
      this.dialogRef.close(product);
    }
  }
}

@Component({
  selector: 'app-admin-products',
  standalone: true,
  imports: [MaterialModule],
  templateUrl: './admin-products.component.html',
  styleUrl: './admin-products.component.scss'
})
export class AdminProductsComponent implements OnInit {
  private _snackBar = inject(MatSnackBar);
  private cd = inject(ChangeDetectorRef);
  private productsService = inject(ProductControllerService);
  private dialog = inject(MatDialog);

  @ViewChild(MatTable) table!: MatTable<Product>;

  products: Product[] = [];
  displayedColumns: string[] = [
    'id',
    'name',
    'basePrice',
    'description',
    'status',
    'actions',
  ];

  // Controle de visualização
  currentView: 'all' | 'active' = 'all';
  selectedTabIndex: number = 0;

  async ngOnInit() {
    await this.loadProducts();
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;

    switch (index) {
      case 0:
        this.loadProducts();
        break;
      case 1:
        this.loadActiveProducts();
        break;
    }
  }

  async loadProducts() {
    try {
      const response = await firstValueFrom(
        this.productsService.findAll4(0, 100, 'name')
      );

      if (response && response.content) {
        this.products = response.content;
      }

      this.currentView = 'all';
      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao carregar produtos', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao carregar produtos:', error);
    }
  }

  async loadActiveProducts() {
    try {
      const response = await firstValueFrom(
        this.productsService.findAllActive()
      );
      this.products = response;
      this.currentView = 'active';
      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao carregar produtos ativos', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro:', error);
    }
  }



  openProductDialog(product?: Product) {
    const dialogRef = this.dialog.open(AdminProductDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { product }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.saveProduct(result);
      }
    });
  }

  async saveProduct(product: Product) {
    try {
      if (product.id) {
        await firstValueFrom(
          this.productsService.update8(product.id, product)
        );
        this._snackBar.open('Produto atualizado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      } else {
        await firstValueFrom(
          this.productsService.save8(product)
        );
        this._snackBar.open('Produto criado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      }

      await this.loadProducts();
    } catch (error) {
      this._snackBar.open('Erro ao salvar produto', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao salvar produto:', error);
    }
  }

  editProduct(product: Product) {
    this.openProductDialog(product);
  }

  async deleteProduct(id: number) {
    const result = await this.confirmDelete();

    if (!result) {
      return;
    }

    try {
      await firstValueFrom(this.productsService.deleteById8(id));
      this._snackBar.open('Produto excluído com sucesso!', 'Fechar', {
        duration: 3000,
      });
      await this.loadProducts();
    } catch (error) {
      this._snackBar.open('Erro ao excluir produto', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao excluir produto:', error);
    }
  }

  async toggleProductStatus(product: Product) {
    if (!product.id) return;

    try {
      if (product.active) {
        await firstValueFrom(this.productsService.deactivate(product.id));
        this._snackBar.open('Produto desativado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      } else {
        await firstValueFrom(this.productsService.activate(product.id));
        this._snackBar.open('Produto ativado com sucesso!', 'Fechar', {
          duration: 3000,
        });
      }
      await this.loadProducts();
    } catch (error) {
      this._snackBar.open('Erro ao alterar status do produto', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao alterar status do produto:', error);
    }
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  getProductStatus(product: Product): 'active' | 'inactive' {
    return product.active ? 'active' : 'inactive';
  }

  private confirmDelete(): Promise<boolean> {
    return new Promise((resolve) => {
      const confirmed = confirm(
        'Tem certeza que deseja excluir este produto?\n\n' +
        'Esta ação não pode ser desfeita.'
      );
      resolve(confirmed);
    });
  }
}
